import { useState } from 'react';
import { Event, Invite } from '../lib/types';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import AddressCard from './AddressCard';
import AdvertisementPlaceholder from './AdvertisementPlaceholder';
import CircleAlertAdvertisement from './CircleAlertAdvertisement';
import EventLinks from './EventLinks';
import EventActivity from './EventActivity';

interface EventDetailsProps {
  event: Event;
  invite?: Invite;
  onOpenMap: () => void;
  isPreview?: boolean;
  hideAd?: boolean;
  showCircleAlertAd?: boolean;
}

export default function EventDetails({ event, invite, onOpenMap, isPreview = false, hideAd = false, showCircleAlertAd = false }: EventDetailsProps) {
  const { t } = useTranslation();
  const [isNoteExpanded, setIsNoteExpanded] = useState(false);

  // Check if note is longer than 4 lines (approximately 200 characters)
  const isLongNote = event.event_note && event.event_note.length > 200;

  return (
    <div className="space-y-4">
      {/* Venue Address */}
      <AddressCard event={event} onOpenMap={onOpenMap} />

      {/* Preview of the ad placement shown on invitations from free hosts */}
      {isPreview && !hideAd && <AdvertisementPlaceholder />}

      {/* Live ad placement for invitations from free hosts */}
      {showCircleAlertAd && <CircleAlertAdvertisement />}

      {/* Note from the host */}
      {event.event_note && (
        <div className="bg-white rounded-lg p-4 shadow">
          <div className="flex items-center space-x-2 mb-4">
            <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900">{t('event.noteFromHost')}</h3>
          </div>
          <div className={`text-gray-600 text-sm whitespace-pre-wrap ${!isNoteExpanded && isLongNote ? 'line-clamp-4' : ''}`}>
            {event.event_note}
          </div>
          {isLongNote && (
            <button
              onClick={() => setIsNoteExpanded(!isNoteExpanded)}
              className="text-blue-600 text-sm mt-2 hover:text-blue-700 font-medium"
            >
              {isNoteExpanded ? t('common.showLess', { defaultValue: 'Show less' }) : t('common.showMore', { defaultValue: 'Show more' })}
            </button>
          )}
        </div>
      )}

      {/* Important Links */}
        <EventLinks event={event} isPaidHost={isPreview ? false : hideAd} />

      {/* Event Activity */}
      <EventActivity event={event} invite={invite} isPreview={isPreview} />
    </div>
  );
}
